import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { SectionTitleProps, SectionTitle } from "./SectionTitle";
import {
  ImageWithAspectRatioProps,
  SectionTopPaddingSizeType,
  SectionBottomPaddingSizeType,
} from "./types";
import { ImageWithAspectRatio } from "./ImageWithAspectRatio";
import { sectionWrapper } from "./sectionWrappers";

export const heroArticle = tv({
  base: cn("uipm:font-normal"),
});

const heroArticleContainer = tv({
  base: cn(
    "uipm:flex uipm:flex-col",
    "uipm:md:flex-row uipm:md:gap-[var(--container-gutter)]"
  ),
});

const typographySection = tv({
  base: cn(
    "uipm:flex uipm:flex-col uipm:gap-[var(--margin-margin-default)]",
    "uipm:md:flex-1 uipm:md:min-w-0"
  ),
  variants: {
    textAlignment: {
      top: "uipm:md:self-start",
      center: "uipm:md:self-center",
    },
  },
  defaultVariants: {
    textAlignment: "top",
  },
});

const imageContainer = tv({
  base: cn(
    "uipm:relative uipm:mt-[var(--container-title-gap)]",
    "uipm:w-full",
    "uipm:md:mt-0 uipm:md:flex-1 uipm:md:self-start"
  ),
  variants: {
    pageType: {
      default: cn(
        "uipm:aspect-[4/5]",
        "uipm:md:aspect-[369.00/461.25] uipm:md:max-w-[600px] uipm:md:max-h-[750px]",
        "uipm:lg:aspect-square uipm:lg:max-w-[612px] uipm:lg:max-h-[612px]"
      ),
      article: cn(
        "uipm:aspect-[3/2]",
        "uipm:md:aspect-[3/2]",
        "uipm:lg:aspect-[3/2]"
      ),
    },
  },
  defaultVariants: {
    pageType: "default",
  },
});

export interface HeroArticleProps
  extends Omit<ComponentProps<"section">, "title">,
    VariantProps<typeof heroArticle> {
  sectionTitle?: SectionTitleProps;
  image?: ImageWithAspectRatioProps;
  video?: React.ReactNode;
  textAlignment?: "top" | "center";
  topPadding?: SectionTopPaddingSizeType;
  bottomPadding?: SectionBottomPaddingSizeType;
  pageType?: "default" | "article";
}

export const HeroArticle: React.FC<HeroArticleProps> = ({
  sectionTitle,
  image,
  video,
  textAlignment = "top",
  topPadding = "none",
  bottomPadding = "none",
  pageType = "default",
  className,
  id,
  ...props
}) => {
  return (
    <section
      id={id}
      className={sectionWrapper({
        backgroundColor: "none",
        topPadding,
        bottomPadding,
        pageType,
      })}
      {...props}
    >
      <div
        className={cn(
          heroArticle(),
          "uipm:max-w-[1440px] uipm:mx-auto",
          className
        )}
      >
        <div
          className={cn(
            heroArticleContainer(),
            pageType === "article"
              ? "uipm:px-[var(--container-margin)]"
              : "uipm:px-[var(--container-card-large-padding)]"
          )}
        >
          {/* Typography Section */}
          <div className={typographySection({ textAlignment })}>
            {/* Section Title */}
            {sectionTitle && (
              <SectionTitle
                {...sectionTitle}
                className={cn(
                  "uipm:text-[var(--typography-colors-text-heading)]",
                  sectionTitle.className
                )}
              />
            )}
          </div>

          {/* Image/Video Section */}
          {(image || video) && (
            <div className={imageContainer({ pageType })}>
              {video ? (
                video
              ) : image ? (
                <ImageWithAspectRatio
                  {...image}
                  // sizes="(max-width: 768px) 100vw, (max-width: 1024px) 600px, 612px"
                  borderRadius="var(--radius-radius-sm)"
                  height="auto"
                />
              ) : null}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export type HeroArticleVariants = VariantProps<typeof heroArticle>;
